/**
 * Context for managing sysadmin user emulation state.
 *
 * When a sysadmin emulates a user, they receive a special JWT token that
 * allows them to act as that user while maintaining full audit trails.
 *
 * Unlike ActingAsContext (per-request header), emulation uses JWT tokens
 * and is session-based.
 */
import { createContext, useContext, useState, useCallback, useEffect, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { apiPost } from '../api/axiosInstance';
import { offlineStorage } from '../offline/OfflineStorageService';
import { resolveEmulationLandingPath } from '../utils/emulationLandingPath';
import { persistKnownLanguage, clearKnownLanguageCookie } from '../../i18n';
// Leaf module — imported directly (not via the `shared/auth` barrel) because
// AuthContext imports this file's `clearEmulationData`.
import { AUTH_STORAGE_KEYS, EMULATION_STORAGE_KEYS } from '../auth/storageKeys';
import { DEFAULT_RETURN_PATH, buildReturnPath } from '../auth/returnUrl';

// The app UI language is a single global localStorage key (i18n's
// `lookupLocalStorage`), NOT scoped per user. Without save/restore it leaks
// across the emulation boundary: a language change made while emulating would
// stick on the sysadmin's own session after ending. Mirror the token/user
// save+restore so the sysadmin's language survives an emulation round-trip.
const LANGUAGE_KEY = 'panluma_language';

/**
 * Clear all emulation data from localStorage.
 * Call this when user logs out or session expires to prevent
 * showing emulation banner on login page.
 */
export function clearEmulationData(): void {
  localStorage.removeItem(EMULATION_STORAGE_KEYS.session);
  localStorage.removeItem(EMULATION_STORAGE_KEYS.token);
  localStorage.removeItem(EMULATION_STORAGE_KEYS.originalToken);
  localStorage.removeItem(EMULATION_STORAGE_KEYS.originalUser);
  localStorage.removeItem(EMULATION_STORAGE_KEYS.originalLanguage);
  localStorage.removeItem(EMULATION_STORAGE_KEYS.returnUrl);
}

/** Fired when an emulation ended because its token died rather than on request. */
export const EMULATION_EXPIRED_EVENT = 'panluma:emulation-expired';

/**
 * Payload of {@link EMULATION_EXPIRED_EVENT}. `endExpiredEmulation()` runs
 * outside React and cannot navigate, so it hands the resolved destination to
 * `EmulationProvider` rather than leaving it to re-derive one from storage it
 * has already cleared.
 */
export interface EmulationExpiredDetail {
  /** Same-origin path to land the sysadmin on — their page at activation. */
  returnTo: string;
}

/**
 * One-shot flag read by `EmulationProvider` after the reload, so the operator is
 * told why their emulation ended instead of just finding themselves back in the
 * sysadmin console.
 */
const EMULATION_EXPIRED_NOTICE_KEY = 'panluma_emulation_expired_notice';

/**
 * Put the sysadmin's own access token back (the snapshot taken at activation).
 * Returns false when there was no snapshot to restore.
 */
function restoreOriginalAccessToken(): boolean {
  const originalToken = localStorage.getItem(EMULATION_STORAGE_KEYS.originalToken);
  if (!originalToken) return false;
  localStorage.setItem(AUTH_STORAGE_KEYS.accessToken, originalToken);
  return true;
}

/**
 * Where the sysadmin goes when this emulation finishes: the page they were on
 * when they started it, resolved to a same-origin path.
 *
 * Every tear-down reads this, and every one of them must read it BEFORE
 * `clearEmulationData()` — which drops the capture along with the rest of the
 * session. The capture round-trips through localStorage, so it goes through the
 * same resolver the landing URL uses (`//evil.com`, foreign origins).
 */
function readEmulationReturnTarget(): string {
  return resolveEmulationLandingPath(
    localStorage.getItem(EMULATION_STORAGE_KEYS.returnUrl) ?? undefined,
    window.location.origin,
  );
}

/**
 * Restore the sysadmin's cached user and UI language from the activation
 * snapshot. The language restore undoes any change made while emulating: the
 * global `panluma_language` key is not scoped per user, and i18n now reads the
 * cross-subdomain cookie AHEAD of localStorage, so both have to be put back or
 * the emulated user's language briefly wins on the sysadmin after the reload.
 */
function restoreSysadminUserAndLanguage(): void {
  const originalUser = localStorage.getItem(EMULATION_STORAGE_KEYS.originalUser);
  if (originalUser) {
    localStorage.setItem(AUTH_STORAGE_KEYS.user, originalUser);
  } else {
    // If no original user saved, clear user data so AuthContext fetches fresh
    localStorage.removeItem(AUTH_STORAGE_KEYS.user);
  }

  const originalLanguage = localStorage.getItem(EMULATION_STORAGE_KEYS.originalLanguage);
  if (originalLanguage) {
    localStorage.setItem(LANGUAGE_KEY, originalLanguage);
    persistKnownLanguage(originalLanguage);
  } else {
    // Sysadmin had no explicit language — drop the emulated override so i18n
    // falls back to its own detection rather than keeping the emulated value.
    localStorage.removeItem(LANGUAGE_KEY);
    clearKnownLanguageCookie();
  }
}

/**
 * Latched by `endExpiredEmulation()` for the rest of this page's lifetime: an
 * emulation has ended because its token died, the sysadmin's own session has been
 * restored in storage, and a reload back into it is under way.
 *
 * Read by the axios refresh path (`shared/api/axiosInstance`), which must then
 * refuse to refresh rather than call `handleSessionExpired()` — that wipes
 * localStorage, and in this window what it would wipe is the just-restored
 * sysadmin session.
 *
 * It lives HERE, on the single teardown function, rather than in the caller that
 * needs it: there are five entry points (both axios interceptors, the
 * `EmulationProvider` expiry interval, the two `AuthContext` bootstrap paths), and
 * a latch owned by one of them is a latch the other four silently skip — which is
 * exactly the divergence this replaced (WI 177dc960).
 */
let emulationTeardownStarted = false;

/** Whether an emulation tear-down has begun in this page. See the latch above. */
export function isEmulationTeardownStarted(): boolean {
  return emulationTeardownStarted;
}

/**
 * End an emulation whose access token is dead — it expired, or the server
 * answered 401 with it.
 *
 * This is the defined outcome for a dying emulation token, and the reason every
 * refresh site gates on `isEmulationAccessToken()` first: the stored refresh
 * token belongs to the SYSADMIN, so exchanging it would resume their session
 * silently, behind a still-showing emulation banner (WI 59902ad6). Instead the
 * sysadmin's own context is restored explicitly, the banner state goes with it,
 * and the operator is told what happened.
 *
 * The backend `/emulation/end` call is deliberately skipped: it authenticates
 * with the very token that just died, so it could only 401 — and the server-side
 * session has already lapsed on its own TTL.
 *
 * Safe to call from anywhere (it is a no-op when no emulation is active) and
 * idempotent, so concurrent in-flight requests all failing at once end the
 * session once. Callable outside React — `EmulationProvider` listens for the
 * event and performs the navigation.
 */
export function endExpiredEmulation(): void {
  if (!localStorage.getItem(EMULATION_STORAGE_KEYS.token)) return;

  // Before any storage is touched: from here on a concurrent refresh must not be
  // allowed to expire the session out from under the restore below.
  emulationTeardownStarted = true;

  // Read before `clearEmulationData()` drops it. An emulation that ran out of
  // time is still an emulation the sysadmin FINISHED, so it owes them the same
  // way back as the button does — landing them on the dashboard is how the
  // reproduction they were in the middle of gets lost.
  const returnTo = readEmulationReturnTarget();

  const restored = restoreOriginalAccessToken();
  restoreSysadminUserAndLanguage();
  clearEmulationData();

  // Fail CLOSED when there was no snapshot to restore. Leaving the dead emulation
  // JWT in place would be worse than useless: `clearEmulationData()` has just
  // removed the mirror key, so `isEmulationAccessToken()` would report FALSE for it
  // and the next bootstrap would treat it as an ordinary expired session — reaching
  // the unguarded refresh path and minting a sysadmin pair off the sysadmin refresh
  // token. That is the very defect this module exists to prevent, re-entered through
  // the one path that skips the guard. Dropping the session sends the operator to
  // the login page instead, which is recoverable and never the wrong actor.
  if (!restored) {
    localStorage.removeItem(AUTH_STORAGE_KEYS.accessToken);
    localStorage.removeItem(AUTH_STORAGE_KEYS.user);
  }

  sessionStorage.setItem(EMULATION_EXPIRED_NOTICE_KEY, '1');
  window.dispatchEvent(
    new CustomEvent<EmulationExpiredDetail>(EMULATION_EXPIRED_EVENT, {
      detail: { returnTo },
    }),
  );
}

export interface EmulationSession {
  session_id: string;
  sysadmin_user_id: string;
  sysadmin_email: string;
  emulated_user_id: string;
  emulated_user_email: string;
  emulated_user_name: string;
  emulated_tenant_id: string;
  emulated_tenant_name: string;
  justification: string;
  authority: string;
  started_at: string;
  expires_at: string;
  is_active: boolean;
  /**
   * The emulated tenant's own workspace origin, when it differs from the host
   * this session runs on (the sysadmin console). Links generated FOR the
   * emulated tenant must carry it — see `getWorkspaceOrigin()`. Null/absent
   * means the current origin is already correct.
   */
  workspace_origin?: string | null;
}

export interface ActivateEmulationOptions {
  /**
   * URL the emulated user should land on once emulation activates. Resolved to
   * a path on the CURRENT origin (see resolveEmulationLandingPath) — a capture
   * from another PanLuma host is followed by path, one from anywhere else is
   * ignored in favour of the dashboard.
   */
  landingUrl?: string;
}

interface EmulationContextType {
  // Current emulation session (null if not emulating)
  emulationSession: EmulationSession | null;
  // Whether we're in emulation mode
  isEmulating: boolean;
  // Start emulating a user (called after API returns token)
  activateEmulation: (
    session: EmulationSession,
    emulationToken: string,
    options?: ActivateEmulationOptions,
  ) => Promise<void>;
  // End emulation and return to sysadmin context
  endEmulation: () => Promise<void>;
  // Check if emulation is expired
  isEmulationExpired: () => boolean;
}

/**
 * The emulation session stored by a previous page, or null when there is none,
 * it is unreadable, or it has already expired.
 *
 * Deletes NOTHING. An expired stored session still has a tear-down to run —
 * `endExpiredEmulation()`, from the boot effect in `EmulationProvider` — and
 * deleting the keys here instead is what used to strand the dead emulation JWT
 * in `AUTH_STORAGE_KEYS.accessToken` with its mirror key gone: no banner, no way
 * to end the session, the operator still acting as the emulated user, and
 * `isEmulationAccessToken()` answering FALSE for the very token it exists to
 * recognise, so the next refresh minted a SYSADMIN pair off the sysadmin's
 * refresh token (the WI 59902ad6 defect, re-entered through the one path that
 * skipped the guard).
 */
function readStoredEmulationSession(): EmulationSession | null {
  const stored = localStorage.getItem(EMULATION_STORAGE_KEYS.session);
  if (!stored) return null;
  try {
    const session = JSON.parse(stored) as EmulationSession;
    return new Date(session.expires_at) < new Date() ? null : session;
  } catch {
    return null;
  }
}

const EmulationContext = createContext<EmulationContextType | undefined>(undefined);

interface EmulationProviderProps {
  children: ReactNode;
}

export function EmulationProvider({ children }: EmulationProviderProps) {
  const { t } = useTranslation();
  // Initialize from localStorage (persists across tabs). An expired session
  // reads as "not emulating" here and is torn down by the boot effect below.
  const [emulationSession, setEmulationSession] = useState<EmulationSession | null>(
    readStoredEmulationSession,
  );

  // An emulation that ended because its token died (here, in an axios
  // interceptor, or on the boot check below) has already restored the
  // sysadmin's own session in storage — all that is left is to drop the React
  // state and reload into the page they started from, which the tear-down
  // resolved before it cleared the capture.
  //
  // Declared FIRST among this component's effects on purpose: mount effects run
  // in declaration order, and the boot check below dispatches this event during
  // that same pass.
  useEffect(() => {
    const handleExpired = (event: Event) => {
      const detail = (event as CustomEvent<EmulationExpiredDetail>).detail;
      setEmulationSession(null);
      // Re-resolved rather than trusted. `dispatchEvent` is an ordinary
      // same-origin DOM API, so `detail` is not privileged just because OUR
      // tear-down is its only intended producer — and this value is assigned
      // straight to `location.href`, where `//evil.com/x` is a protocol-relative
      // reference the browser follows off-site. The resolver is idempotent over
      // its own output, so re-running it costs nothing and keeps the safety
      // property at the point of use.
      window.location.href = detail?.returnTo
        ? resolveEmulationLandingPath(detail.returnTo, window.location.origin)
        : DEFAULT_RETURN_PATH;
    };
    window.addEventListener(EMULATION_EXPIRED_EVENT, handleExpired);
    return () => window.removeEventListener(EMULATION_EXPIRED_EVENT, handleExpired);
  }, []);

  // Tell the operator why they are back in the sysadmin console. The flag is set
  // by whichever page's tear-down ended the emulation, before the reload that
  // brought us here, so it is read once on mount.
  //
  // Declared BEFORE the boot tear-down below, and that is what makes it visible:
  // the tear-down raises this flag for the page it is navigating TO, so reading
  // it afterwards would consume it on the way out and the destination would
  // render in silence — the operator finding themselves back in the sysadmin
  // console with nothing saying why, which is the whole thing this notice exists
  // to prevent.
  useEffect(() => {
    if (sessionStorage.getItem(EMULATION_EXPIRED_NOTICE_KEY)) {
      sessionStorage.removeItem(EMULATION_EXPIRED_NOTICE_KEY);
      toast.info(t('admin:emulation.expiredNotice'));
    }
  }, [t]);

  // A session that was ALREADY past its expiry when this page loaded — the
  // sysadmin left the tab open over lunch and came back, or reloaded. It goes
  // through the one defined tear-down like every other dead emulation token, so
  // the sysadmin's own context comes back and they land where they started.
  //
  // Must run BEFORE the persistence effect below, which deletes the emulation
  // token `endExpiredEmulation()` keys off — and AFTER the notice effect above,
  // whose flag it raises.
  useEffect(() => {
    if (!localStorage.getItem(EMULATION_STORAGE_KEYS.session)) return;
    if (readStoredEmulationSession()) return;

    if (localStorage.getItem(EMULATION_STORAGE_KEYS.token)) {
      endExpiredEmulation();
      return;
    }
    // No emulation token to hand back from, so there is nothing to restore and
    // nowhere to send anyone — just drop the dead capture so the next emulation
    // cannot inherit it.
    localStorage.removeItem(EMULATION_STORAGE_KEYS.returnUrl);
  }, []);

  // Persist to localStorage when changed
  useEffect(() => {
    if (emulationSession) {
      localStorage.setItem(EMULATION_STORAGE_KEYS.session, JSON.stringify(emulationSession));
    } else {
      localStorage.removeItem(EMULATION_STORAGE_KEYS.session);
      localStorage.removeItem(EMULATION_STORAGE_KEYS.token);
    }
  }, [emulationSession]);

  // Check expiration periodically. The axios interceptors reach the same
  // conclusion sooner (on the first request made with a dead emulation token),
  // so both go through endExpiredEmulation() — one defined outcome, whichever
  // notices first, and one tear-down latch (see above) whichever gets there.
  useEffect(() => {
    if (!emulationSession) return;

    const checkExpiration = () => {
      if (new Date(emulationSession.expires_at) < new Date()) {
        endExpiredEmulation();
      }
    };

    const interval = setInterval(checkExpiration, 60000); // Check every minute
    return () => clearInterval(interval);
  }, [emulationSession]);


  const activateEmulation = useCallback(async (
    session: EmulationSession,
    emulationToken: string,
    options?: ActivateEmulationOptions,
  ) => {
    // Save original token for restoration (using the correct key that axiosInstance uses)
    const currentToken = localStorage.getItem(AUTH_STORAGE_KEYS.accessToken);
    if (currentToken) {
      localStorage.setItem(EMULATION_STORAGE_KEYS.originalToken, currentToken);
    }

    // Save original user data for restoration
    const currentUser = localStorage.getItem(AUTH_STORAGE_KEYS.user);
    if (currentUser) {
      localStorage.setItem(EMULATION_STORAGE_KEYS.originalUser, currentUser);
    }

    // Save the sysadmin's UI language so it can be restored when emulation ends
    // (the global panluma_language key would otherwise leak the emulated
    // session's language back onto the sysadmin). If the sysadmin had no
    // explicit language, endEmulation drops the override entirely.
    localStorage.setItem(EMULATION_STORAGE_KEYS.originalLanguage, localStorage.getItem(LANGUAGE_KEY) ?? '');

    // Remember where the sysadmin was so ending the emulation returns them
    // there (the user detail page, the support ticket they were reproducing,
    // …) instead of dumping them on the dashboard. Only captured when NOT
    // already emulating, so a session started from inside another one can't
    // overwrite the sysadmin's own page with an emulated one.
    if (!localStorage.getItem(EMULATION_STORAGE_KEYS.token)) {
      localStorage.setItem(
        EMULATION_STORAGE_KEYS.returnUrl,
        buildReturnPath(window.location),
      );
    }

    // The emulation session lives on THIS origin — an emulation JWT cannot be
    // transferred cross-domain — so a landing URL captured on another PanLuma
    // host is followed by path here. See resolveEmulationLandingPath.
    const landingTarget = resolveEmulationLandingPath(
      options?.landingUrl,
      window.location.origin,
    );

    // Same-origin activation
    localStorage.setItem(AUTH_STORAGE_KEYS.accessToken, emulationToken);
    localStorage.setItem(EMULATION_STORAGE_KEYS.token, emulationToken);

    // Clear the cached user data so AuthContext fetches fresh data for the emulated user
    localStorage.removeItem(AUTH_STORAGE_KEYS.user);

    // Update session state
    setEmulationSession(session);

    // Clear the persisted React Query cache to prevent showing sysadmin's cached data
    await offlineStorage.clearAllData();

    // Reload to refresh all data with new context
    window.location.href = landingTarget;
  }, []);

  const endEmulation = useCallback(async () => {
    // Resolve the way back BEFORE anything awaits. The end request below goes
    // out carrying the emulation access token, and a dead or rejected one trips
    // `endExpiredEmulation()` inside the axios interceptors — which clears the
    // capture. Reading it afterwards dropped the sysadmin on the dashboard in
    // exactly the case where they most need the page they came from: a long
    // reproduction session whose token ran out while they worked.
    const returnTarget = readEmulationReturnTarget();

    try {
      // Call API to end session and get return token
      interface EndEmulationResponse {
        data: {
          return_token?: string;
        };
      }
      const response = await apiPost<EndEmulationResponse>('/api/v1/admin/emulation/end');
      const returnToken = response.data?.return_token;

      // Restore original token or use return token
      if (returnToken) {
        localStorage.setItem(AUTH_STORAGE_KEYS.accessToken, returnToken);
      } else {
        restoreOriginalAccessToken();
      }
    } catch (error) {
      console.error('Failed to end emulation:', error);
      restoreOriginalAccessToken();
    }

    // Restore the sysadmin's cached user and UI language. The reload below lets
    // i18n re-read panluma_language.
    restoreSysadminUserAndLanguage();

    // Clean up emulation-specific storage
    clearEmulationData();
    setEmulationSession(null);

    // Clear the persisted React Query cache to prevent showing emulated user's cached data
    await offlineStorage.clearAllData();

    // Reload to refresh all data with sysadmin context
    window.location.href = returnTarget;
  }, []);

  const isEmulationExpired = useCallback((): boolean => {
    if (!emulationSession) return false;
    return new Date(emulationSession.expires_at) < new Date();
  }, [emulationSession]);

  const value: EmulationContextType = {
    emulationSession,
    isEmulating: emulationSession !== null && !isEmulationExpired(),
    activateEmulation,
    endEmulation,
    isEmulationExpired,
  };

  return (
    <EmulationContext.Provider value={value}>
      {children}
    </EmulationContext.Provider>
  );
}

export function useEmulation(): EmulationContextType {
  const context = useContext(EmulationContext);
  if (!context) {
    throw new Error('useEmulation must be used within an EmulationProvider');
  }
  return context;
}

/**
 * Check if currently in emulation mode.
 * Use this in non-component code.
 */
export function isInEmulationMode(): boolean {
  const stored = localStorage.getItem(EMULATION_STORAGE_KEYS.session);
  if (stored) {
    try {
      const session = JSON.parse(stored);
      return new Date(session.expires_at) > new Date();
    } catch {
      return false;
    }
  }
  return false;
}

/**
 * Get the current emulation session.
 * Use this in non-component code.
 */
export function getEmulationSession(): EmulationSession | null {
  const stored = localStorage.getItem(EMULATION_STORAGE_KEYS.session);
  if (stored) {
    try {
      const session = JSON.parse(stored);
      if (new Date(session.expires_at) > new Date()) {
        return session;
      }
    } catch {
      return null;
    }
  }
  return null;
}
